import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Skeleton,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as PreviewIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  FilterList as FilterIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  TikTok as TikTokIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { useDemoMode } from '../providers/DemoModeProvider';
import { platformStrategyApi, trackStrategyEvent } from '../services/platformStrategyApi';
import { getPlatformIcon, getPlatformColor } from '../utils/platformUtils';

// Mock data for demonstration
const mockStrategyData = {
  overview: {
    totalStrategies: 8,
    activeStrategies: 6,
    avgPerformanceBoost: 45,
    topPerformingPlatform: 'linkedin'
  },
  strategies: [
    {
      id: 1,
      platform: 'linkedin',
      strategyType: 'timing_optimization',
      title: 'Optimal LinkedIn Posting Schedule',
      description: 'Post on Tuesdays and Thursdays at 9 AM for 3x better engagement with your B2B audience.',
      confidence: 0.92,
      performanceImpact: 185,
      recommendations: [
        'Post between 8-10 AM on weekdays',
        'Focus on educational content',
        'Use 3-5 professional hashtags',
        'Include industry insights'
      ],
      timing: {
        bestDays: ['Tuesday', 'Thursday', 'Wednesday'],
        bestHours: ['9', '14', '16'],
        timezone: 'UTC',
        lastUpdated: '2024-01-20T10:00:00Z'
      },
      hashtags: {
        recommended: ['#B2BSales', '#Leadership', '#ProductivityTips', '#BusinessGrowth'],
        trending: ['#AI', '#Innovation', '#DigitalTransformation'],
        performance: { '#B2BSales': 8.5, '#Leadership': 7.2, '#AI': 9.1 }
      },
      isActive: true,
      lastUpdated: '2024-01-20T10:00:00Z'
    },
    {
      id: 2,
      platform: 'twitter',
      strategyType: 'content_optimization',
      title: 'Twitter Engagement Strategy',
      description: 'Short, punchy posts with trending hashtags perform 200% better for your audience.',
      confidence: 0.87,
      performanceImpact: 156,
      recommendations: [
        'Keep posts under 120 characters',
        'Use 1-2 trending hashtags',
        'Include images or GIFs',
        'Post 3-5 times per day'
      ],
      timing: {
        bestDays: ['Monday', 'Wednesday', 'Friday'],
        bestHours: ['12', '17', '20'],
        timezone: 'UTC',
        lastUpdated: '2024-01-19T15:00:00Z'
      },
      hashtags: {
        recommended: ['#TechNews', '#StartupLife', '#Innovation'],
        trending: ['#AI', '#Blockchain', '#SaaS'],
        performance: { '#TechNews': 7.8, '#AI': 8.9, '#SaaS': 6.5 }
      },
      isActive: true,
      lastUpdated: '2024-01-19T15:00:00Z'
    },
    {
      id: 3,
      platform: 'instagram',
      strategyType: 'visual_optimization',
      title: 'Instagram Visual Strategy',
      description: 'Bright, colorful images with user-generated content increase engagement by 120%.',
      confidence: 0.75,
      performanceImpact: 95,
      recommendations: [
        'Use bright, high-contrast images',
        'Include faces in photos',
        'Post stories daily',
        'Use location tags'
      ],
      timing: {
        bestDays: ['Saturday', 'Sunday', 'Wednesday'],
        bestHours: ['11', '14', '19'],
        timezone: 'UTC',
        lastUpdated: '2024-01-18T12:00:00Z'
      },
      hashtags: {
        recommended: ['#LifestyleBrand', '#BehindTheScenes', '#UserGenerated'],
        trending: ['#Aesthetic', '#Inspiration', '#Community'],
        performance: { '#LifestyleBrand': 6.2, '#BehindTheScenes': 7.5, '#Community': 8.1 }
      },
      isActive: true,
      lastUpdated: '2024-01-18T12:00:00Z'
    }
  ],
  timingAnalysis: {
    linkedin: {
      bestDays: ['Tuesday', 'Thursday'],
      bestHours: ['9', '14'],
      performanceByDay: { 'Monday': 65, 'Tuesday': 95, 'Wednesday': 78, 'Thursday': 92, 'Friday': 45 },
      performanceByHour: { '9': 95, '10': 85, '14': 90, '16': 75 }
    },
    twitter: {
      bestDays: ['Monday', 'Wednesday'],
      bestHours: ['12', '17'],
      performanceByDay: { 'Monday': 88, 'Tuesday': 72, 'Wednesday': 85, 'Thursday': 69, 'Friday': 82 },
      performanceByHour: { '12': 88, '13': 79, '17': 85, '20': 78 }
    }
  }
};

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`strategy-tabpanel-${index}`}
      aria-labelledby={`strategy-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PlatformStrategyPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { isDemoMode } = useDemoMode();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [timingAnalysis, setTimingAnalysis] = useState({});
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [strategyDialog, setStrategyDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    platform: 'all',
    strategyType: 'all',
    isActive: true
  });
  const [refreshing, setRefreshing] = useState(false);

  // Load strategy data
  useEffect(() => {
    loadStrategyData();
  }, [filters, isDemoMode]);

  useEffect(() => {
    if (user) {
      trackStrategyEvent('dashboard_viewed', {
        userId: user.id,
        timestamp: new Date().toISOString()
      });
    }
  }, [user]);

  const loadStrategyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // If demo mode is enabled, use mock data directly
      if (isDemoMode) {
        console.log('🎬 Demo mode enabled, using mock strategy data');
        setOverview(mockStrategyData.overview || {});
        setStrategies(mockStrategyData.strategies || []);
        setTimingAnalysis(mockStrategyData.timingAnalysis || {});
        setLoading(false);
        return;
      }

      // Try to load real data from API
      try {
        const [strategiesResponse, analyticsResponse] = await Promise.all([
          platformStrategyApi.getStrategies(filters),
          platformStrategyApi.getCrossPlatformAnalytics({ timeRange: '30d' })
        ]);

        if (strategiesResponse.success && analyticsResponse.success) {
          setStrategies(strategiesResponse.data);
          setOverview(analyticsResponse.data.overview);
          setTimingAnalysis(analyticsResponse.data.timingAnalysis);
          console.log('✅ Loaded real strategy data');
        } else {
          throw new Error('Invalid API response');
        }
      } catch (apiError) {
        console.warn('⚠️ Strategy API failed, using mock data:', apiError.message);
        // Fallback to mock data
        setOverview(mockStrategyData.overview || {});
        setStrategies(mockStrategyData.strategies || []);
        setTimingAnalysis(mockStrategyData.timingAnalysis || {});
      }

    } catch (err) {
      console.error('Failed to load strategy data:', err);
      setError('Failed to load platform strategy data');
      // Use mock data as final fallback
      setOverview(mockStrategyData.overview || {});
      setStrategies(mockStrategyData.strategies || []);
      setTimingAnalysis(mockStrategyData.timingAnalysis || {});
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    trackStrategyEvent('refresh_clicked', { userId: user?.id });
    await loadStrategyData();
    setRefreshing(false);
  };

  const handleStrategyClick = (strategy) => {
    setSelectedStrategy(strategy);
    setStrategyDialog(true);
    
    trackStrategyEvent('strategy_viewed', {
      userId: user?.id,
      strategyId: strategy?.id,
      platform: strategy?.platform,
      strategyType: strategy?.strategyType
    });
  };

  const handleApplyStrategy = async (strategy) => {
    try {
      if (!strategy?.id) {
        console.error('Cannot apply strategy: Invalid strategy object');
        return;
      }

      await platformStrategyApi.applyStrategy(strategy.id, {
        targetType: 'campaign',
        targetId: 'current',
        applyTiming: true,
        applyHashtags: true,
        applyGuidelines: true
      });

      trackStrategyEvent('strategy_applied', {
        userId: user?.id,
        strategyId: strategy?.id,
        platform: strategy?.platform
      });

      // Show success message or redirect
      console.log('Strategy applied successfully');
    } catch (error) {
      console.error('Failed to apply strategy:', error);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const formatPerformanceImpact = (impact) => {
    return impact > 0 ? `+${impact}%` : `${impact}%`;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="text" width={200} height={24} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Platform Strategy
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              AI-powered platform-specific strategies for maximum engagement
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {/* Filter dialog */}}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {(error || isDemoMode) && (
          <Alert severity={isDemoMode ? "info" : "warning"} sx={{ mb: 2 }}>
            {isDemoMode 
              ? "🎬 Demo Mode: Showing sample platform strategy data to explore features" 
              : "Using sample data for platform strategy features. Backend integration in progress."
            }
          </Alert>
        )}
      </Box>

      {/* Overview Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Active Strategies
                  </Typography>
                  <Typography variant="h4" component="div">
                    {overview?.activeStrategies || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                  <SpeedIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Strategies
                  </Typography>
                  <Typography variant="h4" component="div">
                    {overview?.totalStrategies || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.light', color: 'info.main' }}>
                  <AssessmentIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Avg Performance Boost
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    +{overview?.avgPerformanceBoost || 0}%
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Top Platform
                  </Typography>
                  <Typography variant="h4" component="div">
                    {overview?.topPerformingPlatform?.toUpperCase() || 'N/A'}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main' }}>
                  <StarIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Strategy Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Active Strategies" icon={<SpeedIcon />} />
          <Tab label="Timing Analysis" icon={<ScheduleIcon />} />
          <Tab label="Platform Guidelines" icon={<InfoIcon />} />
        </Tabs>

        {/* Active Strategies Tab */}
        <TabPanel value={activeTab} index={0}>
          {strategies.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SpeedIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No strategies found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first platform strategy to optimize your content performance
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }}>
                Create Strategy
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {strategies.filter(strategy => strategy && strategy.platform).map((strategy) => (
                <Grid item xs={12} md={6} lg={4} key={strategy.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => handleStrategyClick(strategy)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: getPlatformColor(strategy?.platform || 'twitter'),
                            mr: 2,
                            width: 40,
                            height: 40
                          }}
                        >
                          {React.createElement(getPlatformIcon(strategy?.platform || 'twitter'))}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" noWrap>
                            {strategy?.title || 'Untitled Strategy'}
                          </Typography>
                          <Chip
                            label={(strategy?.strategyType || 'general').replace('_', ' ').toUpperCase()}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {strategy?.description || 'No description available'}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip
                          label={`${Math.round((strategy?.confidence || 0) * 100)}% confidence`}
                          size="small"
                          color={getConfidenceColor(strategy?.confidence || 0)}
                        />
                        <Chip
                          label={formatPerformanceImpact(strategy?.performanceImpact || 0)}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                        {strategy?.isActive && (
                          <Chip
                            label="Active"
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Updated {strategy?.lastUpdated ? new Date(strategy.lastUpdated).toLocaleDateString() : 'Unknown'}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyStrategy(strategy);
                          }}
                        >
                          Apply
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Timing Analysis Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            {Object.entries(timingAnalysis || {}).map(([platform, data]) => (
              <Grid item xs={12} md={6} key={platform}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: getPlatformColor(platform || 'twitter'),
                          mr: 2
                        }}
                      >
                        {React.createElement(getPlatformIcon(platform || 'twitter'))}
                      </Avatar>
                      <Typography variant="h6">
                        {(platform || 'Unknown').charAt(0).toUpperCase() + (platform || 'unknown').slice(1)} Timing
                      </Typography>
                    </Box>
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Best Days:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {(data?.bestDays || []).map((day) => (
                        <Chip key={day} label={day} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Best Hours:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {(data?.bestHours || []).map((hour) => (
                        <Chip key={hour} label={`${hour}:00`} size="small" color="secondary" variant="outlined" />
                      ))}
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary">
                      Based on last 30 days of performance data
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Platform Guidelines Tab */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Platform Guidelines
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Coming soon: Platform-specific content guidelines and best practices.
          </Typography>
        </TabPanel>
      </Paper>

      {/* Strategy Detail Dialog */}
      <Dialog open={strategyDialog} onClose={() => setStrategyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedStrategy && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: getPlatformColor(selectedStrategy?.platform || 'twitter'),
                  mr: 2
                }}
              >
                {React.createElement(getPlatformIcon(selectedStrategy?.platform || 'twitter'))}
              </Avatar>
              {selectedStrategy?.title || 'Strategy Details'}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedStrategy && (
            <Box>
              <Typography variant="body1" paragraph>
                {selectedStrategy?.description || 'No description available'}
              </Typography>
              
              <Typography variant="h6" gutterBottom>
                Recommendations:
              </Typography>
              <List dense>
                {(selectedStrategy?.recommendations || []).map((recommendation, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={recommendation} />
                  </ListItem>
                ))}
              </List>
              
              {selectedStrategy?.hashtags && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Recommended Hashtags:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(selectedStrategy?.hashtags?.recommended || []).map((hashtag) => (
                      <Chip key={hashtag} label={hashtag} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStrategyDialog(false)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              if (selectedStrategy) {
                handleApplyStrategy(selectedStrategy);
              }
              setStrategyDialog(false);
            }}
          >
            Apply Strategy
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlatformStrategyPage; 